import { appVariant } from '@util/AppUtils';
import { MenoUserData, NervaUserData } from 'hydra-data-types';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import {
  MENO_SELECTOR_CONFIG,
  NERVA_SELECTOR_AI_PERSONALISED_CONFIG,
  NERVA_SELECTOR_CONFIG,
  getUtmTermSearchParam,
  ibsTypeFromSymptoms,
  menopauseTypeFromStage,
} from '../../config/AssessmentUtils';
import { ScreenType, SelectScreenConfig } from '../../config/FlowTypes';
import { MenopauseType } from '../../config/meno/MenoTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { InfoBox } from '../styles/InfoBox';
import { BodyXS, BodyXXS } from '../styles/Typography';
import { NavigationProps } from './FlowNavigation';
import { SelectLayoutConfig, SingleSelectLayout } from './layouts/SingleSelectLayout';
import { SELECT_DELAY_DEFAULT_MS } from './SingleSelectDefaultDelay';
import { onSingleSelectSubmit } from './SingleSelectScreen';

export type ClassifyScreenConfig<ScreenNames extends string> = SelectScreenConfig<
  ScreenType.Classify,
  SelectLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  ClassifyScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ClassifyScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { layout } = props;
  const { body = [] } = layout;

  const userData = useAppStore(AppSelector.userData);
  const stage = (userData as MenoUserData).stage ?? [];
  const menopauseType = menopauseTypeFromStage(stage[0]);

  const nervaSymptoms = (userData as NervaUserData).symptoms ?? [];
  const ibsType = ibsTypeFromSymptoms(nervaSymptoms);
  const isUtmTermHardPersonal = getUtmTermSearchParam() === 'hardpersonal3';

  function menoBodyText() {
    return menopauseType === MenopauseType.Regular
      ? [`${body[1]}`]
      : [`${body[0]} ${menopauseType}?`];
  }

  function nervaBodyText() {
    const symptoms = (userData as NervaUserData).symptoms ?? [];
    const ibsType = ibsTypeFromSymptoms(symptoms);

    return [`${body[0]} ${ibsType}${body[1]}`];
  }

  function onSelected(selected: string) {
    // Small action timeout so user can actually see what option was clicked
    setTimeout(() => onSingleSelectSubmit({ props, selected }), SELECT_DELAY_DEFAULT_MS);
  }

  return (
    <CustomWrapper showClassifications={isUtmTermHardPersonal}>
      <SingleSelectLayout
        onSelected={onSelected}
        {...layout}
        noWrapper={isUtmTermHardPersonal}
        body={appVariant({
          nerva: nervaBodyText(),
          meno: menoBodyText(),
          quit: [],
          pain: [],
        })}
        choices={appVariant({
          nerva: isUtmTermHardPersonal
            ? NERVA_SELECTOR_AI_PERSONALISED_CONFIG[ibsType].options
            : NERVA_SELECTOR_CONFIG[ibsType].options,
          meno: MENO_SELECTOR_CONFIG[menopauseType].options,
          quit: [],
          pain: [],
        })}
      />
      {isUtmTermHardPersonal && (
        <>
          <div style={{ marginTop: '60px' }}>
            <InfoBox
              titleText="It's okay if you're not sure"
              bodyText="It's okay to be unsure of the IBS type, the other answers to other questions will help build your personalized program."
            />
          </div>
          <div
            style={{
              borderTop: '1px solid #e0e0e0',
              marginTop: '20px',
              padding: '20px 0',
              textAlign: 'left',
            }}
          >
            <BodyXS bold>What are IBS classifications?</BodyXS>
            <BodyXXS style={{ marginBottom: '20px' }}>
              IBS classifications are subtypes of Irritable Bowel Syndrome based on stool patterns.
            </BodyXXS>

            <BodyXXS style={{ marginBottom: '16px' }}>
              <strong>IBS-D:</strong> Irritable Bowel Syndrome with diarrhea-predominant bowel
              habits.
            </BodyXXS>

            <BodyXXS style={{ marginBottom: '16px' }}>
              <strong>IBS-C:</strong> Irritable Bowel Syndrome with constipation-predominant bowel
              habits.
            </BodyXXS>

            <BodyXXS>
              <strong>IBS-M:</strong> Irritable Bowel Syndrome with mixed bowel habits (alternating
              between diarrhea and constipation).
            </BodyXXS>
          </div>
        </>
      )}
    </CustomWrapper>
  );
};

const CustomWrapper = styled.div<{ showClassifications: boolean }>`
  @media (min-width: 600px) {
    min-height: ${({ showClassifications }) => (showClassifications ? 'auto' : '812px')};
  }
`;
